<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Routes } from '../../../../../Constants/Routes.ts'
import type { ContractRestructuring } from '../../../../../Types/restructuring'
import SimpleGroupSelect from '../../UI/Form/SimpleGroupSelect.vue'
import LazyContent from '../../UI/Loading/LazyContent.vue'

const props = defineProps<{
  restructuring: ContractRestructuring | null
}>()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const currentMenu = computed(() => availableMenuItems.value.find((item) => item.route.name === route.name))

const menuTitle = computed(() =>
  t('restructuring.detail.id', { id: props.restructuring?.id, title: props.restructuring?.label }),
)
const statusTitle = computed(() =>
  t('restructuring.detail.status.title', {
    status: t(`contract.restructuring.status.${props.restructuring?.status}.label`),
  }),
)

const availableMenuItems = computed(() => [
  {
    label: t('restructuring.detail.menu.information'),
    route: {
      name: Routes.RESTRUCTURING_VIEW_INFORMATION,
    },
  },
  {
    label: t('restructuring.detail.menu.documents'),
    route: {
      name: Routes.RESTRUCTURING_VIEW_DOCUMENTS,
    },
  },
])

function redirectToPage(page: string) {
  const selectedMenu = availableMenuItems.value.find((item) => item.route.name === page)

  if (selectedMenu) {
    router.push(selectedMenu.route)
  }
}
</script>
<template>
  <!-- Desktop menu -->
  <div class="topbar-submenu d-none d-sm-flex flex-column position-sticky">
    <LazyContent :loading="!restructuring">
      <div class="topbar-simulation-title px-3 py-2">
        <div class="row">
          <div class="col-sm-3" v-html="menuTitle" />
          <div class="col-sm-6 text-sm-center" v-html="statusTitle" />
          <div class="col-sm-3"></div>
        </div>
      </div>
      <div class="d-flex justify-content-center align-items-center border-top border-white mx-1 my-1 pt-2">
        <div class="topbar-submenu-link" v-for="menu in availableMenuItems" :key="menu.route.name">
          <RouterLink :to="menu.route" exact-active-class="sub-active">
            {{ menu.label }}
          </RouterLink>
        </div>
      </div>
      <template #loader>
        <div class="topbar-simulation-title text-center px-3 py-2">
          {{ t('restructuring.detail.loading') }}
        </div>
      </template>
    </LazyContent>
  </div>

  <!-- Mobile menu (Bootstrap 4 does not support responsive position) -->
  <div class="topbar-submenu d-flex d-sm-none flex-column position-static">
    <LazyContent :loading="!restructuring">
      <div class="topbar-simulation-title px-3 py-2">
        <div class="row">
          <div class="col-12" v-html="menuTitle" />
          <div class="col-12" v-html="statusTitle" />
        </div>
      </div>
      <div class="m-2 mt-0 border-top border-white pt-2">
        <SimpleGroupSelect :value="currentMenu?.route.name" @transmitted="redirectToPage">
          <option
            v-for="menu in availableMenuItems"
            :key="menu.route.name"
            :selected="route.name === menu.route.name"
            :value="menu.route.name"
          >
            {{ menu.label }}
          </option>
        </SimpleGroupSelect>
      </div>

      <template #loader>
        <div>
          {{ t('restructuring.detail.loading') }}
        </div>
      </template>
    </LazyContent>
  </div>
</template>
